/**
 * Legend Section Component
 * A collapsible accordion section for individual legends
 */

import React from 'react';
import { cn } from '@ais-ui/utils';
import { PiCaretDown, PiCaretRight } from 'react-icons/pi';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ais-ui/ui-core';
import { LegendType, useLegendContainer } from './LegendContainerContext';

interface LegendSectionProps {
  type: LegendType;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}

export const LegendSection: React.FC<LegendSectionProps> = ({
  type,
  title,
  subtitle,
  children,
  className,
}) => {
  const { sectionStates, toggleSection } = useLegendContainer();
  const { expanded } = sectionStates[type];

  return (
    <div className={cn('border-b border-border last:border-b-0', className)}>
      <button
        type="button"
        onClick={() => toggleSection(type)}
        className="flex items-center justify-between w-full px-3 py-2 text-left hover:bg-muted/50 transition-colors"
      >
        <div className="flex items-center gap-2">
          {expanded ? (
            <PiCaretDown className="size-4 text-muted-foreground shrink-0" />
          ) : (
            <PiCaretRight className="size-4 text-muted-foreground shrink-0" />
          )}
          <div className="flex flex-col">
            <span className="font-medium text-sm">{title}</span>
            {subtitle && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground truncate max-w-[200px]">
                    {subtitle}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="start">
                  {subtitle}
                </TooltipContent>
              </Tooltip>
            )}
          </div>
        </div>
      </button>
      
      {expanded && (
        <div className="px-3 pb-3">
          {children}
        </div>
      )}
    </div>
  );
};
